import { useEffect, useMemo, useState } from 'react'
import {
  destinataireDepuisSaisie,
  SAISIE_DESTINATAIRE_VIDE,
  verifierDestinataire,
  type SaisieDestinataire,
} from '../../api/destinataire'
import {
  adressesDuClient,
  adressesDuTelephone,
  assignerCourse,
  creerCourse,
  dansCreneau,
  initiales,
  jourIso,
  lireTension,
  memoriserAdresse,
  type CourseCreee,
  type TensionLue,
} from '../../api/operations'
import { ADRESSE_VIDE, aUnPoint, libelleAdresse, type AdresseConnue, type AdresseSaisie } from '../../api/adresses'
import { calculerPrixOpere, distanceRepliKm, LIBELLE_VEHICULE, VEHICULES, type TypeVehicule } from '../../api/prixOpere'
import { cleTrajet, noterRouteMemorisee, preparerRouteCourse, trajetRoute, type Trajet } from '../../api/itineraire'
import { encoder } from '../../api/pluscode'
import type { Canal, Client, Course, DonneesOps } from '../../api/types'
import { BilanPrix } from '../BilanPrix'
import { CarteCourse } from '../CarteCourse'
import { DemandePosition } from '../DemandePosition'
import { SelecteurAdresse } from '../SelecteurAdresse'
import { chiffresTelephone, fcfa } from '../Format'
import { Icone } from '../Icone'
import { Kpis } from '../Kpis'
import { ListeDeroulante } from '../ListeDeroulante'
import { useDroits } from '../Contexte'
import { ModaleClient, type TypeOuverture } from './Clients'
import { libelleDuPlan } from '../../api/abonnement'

const ETATS_LIVREUR: Record<string, [string, string]> = {
  available: ['Disponible', 'b-green'],
  on_course: ['En course', 'b-gold'],
  offline: ['Hors ligne', 'b-gray'],
}

function s(n: number): string {
  return n > 1 ? 's' : ''
}

/**
 * Recherche d'appelant — TRANSPOSITION de `searchClients()`.
 *
 * Deux seuils distincts, et ils comptent : quatre chiffres pour un numéro,
 * deux caractères pour un nom. Chercher un numéro sur deux chiffres ferait
 * remonter la moitié du fichier pendant que le client est au téléphone.
 */
function chercher(clients: Client[], requete: string): Client[] {
  const chiffres = chiffresTelephone(requete)
  const minuscules = requete.toLowerCase()
  return clients
    .filter(
      (c) =>
        (chiffres.length >= 4 && chiffresTelephone(c.phone).includes(chiffres)) ||
        (minuscules.length >= 2 && c.name.toLowerCase().includes(minuscules)),
    )
    .slice(0, 5)
}

export function EcranPriseAppel({
  donnees,
  orgId,
  surRafraichir,
  surNotifier,
  surAllerDispatch,
  surAssigner,
  surAvancer,
  surAnnuler,
}: {
  donnees: DonneesOps
  orgId: string
  surRafraichir: () => Promise<void>
  surNotifier: (message: string) => void
  surAllerDispatch: () => void
  surAssigner: (courseId: string, livreurId: string) => void
  surAvancer: (course: Course) => void
  surAnnuler: (courseId: string) => void
}) {
  const [requete, setRequete] = useState('')
  const [client, setClient] = useState<Client | null>(null)
  /* Les deux extrémités du trajet : un POINT (épingle, Plus Code, adresse connue) + adresse écrite + repère.
     Le quartier de la liste n'est plus qu'une aide pour recentrer la carte. */
  const [collecte, setCollecte] = useState<AdresseSaisie>(ADRESSE_VIDE)
  const [depot, setDepot] = useState<AdresseSaisie>(ADRESSE_VIDE)
  const [vehicule, setVehicule] = useState<TypeVehicule>('moto')
  const [canal, setCanal] = useState<Canal>('phone')
  /* Prix saisi à la main : UNIQUEMENT quand le trajet n'est pas géolocalisé (ou la grille illisible). */
  const [prixManuel, setPrixManuel] = useState(2500)
  const [accepteSansPoints, setAccepteSansPoints] = useState(false)
  /* Dérogation : réservée au propriétaire (la base ignore le motif de tout autre rôle). */
  const [deroger, setDeroger] = useState(false)
  const [prixDerogation, setPrixDerogation] = useState('')
  const [motifDerogation, setMotifDerogation] = useState('')
  const [adressesClient, setAdressesClient] = useState<AdresseConnue[]>([])
  const [adressesDest, setAdressesDest] = useState<AdresseConnue[]>([])
  /* La course qu'on vient de créer : prix retenu par la base, et demande de position si besoin. */
  const [derniere, setDerniere] = useState<CourseCreee | null>(null)
  /* Destinataire (0028) : par défaut le client reçoit lui-même — case cochée, champs masqués. */
  const [destinataire, setDestinataire] = useState<SaisieDestinataire>(SAISIE_DESTINATAIRE_VIDE)
  const [enCours, setEnCours] = useState(false)
  const [nouveauClient, setNouveauClient] = useState<TypeOuverture | null>(null)
  /* Après création d'une fiche, on la sélectionne dès que le rechargement
     l'a rapportée — comme `openClientModal` → `pickClient` dans l'original. */
  const [aSelectionner, setASelectionner] = useState<string | null>(null)
  const [flash, setFlash] = useState<string | null>(null)
  const droits = useDroits()
  const peutCreer = droits.peut('courses')

  useEffect(() => {
    if (!aSelectionner) return
    const c = donnees.clients.find((x) => x.id === aSelectionner)
    if (c) {
      choisir(c)
      setASelectionner(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aSelectionner, donnees.clients])

  useEffect(() => {
    if (!flash) return
    const t = setTimeout(() => setFlash(null), 1600)
    return () => clearTimeout(t)
  }, [flash])

  const resultats = useMemo(
    () => (requete.trim().length >= 2 ? chercher(donnees.clients, requete.trim()) : []),
    [donnees.clients, requete],
  )

  /* Le supplément ne dépend PAS d'une case à cocher : il découle de l'heure qu'il est et des créneaux du
     client. Ici c'est un APERÇU — la base refait ce calcul à l'insertion (heure de Bamako) et c'est son
     résultat qui fait foi. */
  const verdict = dansCreneau(client ?? undefined)
  const supplement = client && client.type === 'pme' && !verdict.dedans ? client.offslot_surcharge_fcfa : 0

  const tarifs = donnees.tarifs

  /* Majoration de tension (0043) : la demande et l'offre autour du point d'enlèvement sont COMPTÉES PAR LA BASE
     (RPC `tension_operee`), relues toutes les 30 s ; le prix est ensuite calculé ici avec le jumeau TypeScript.
     La base recompte à la création : c'est son résultat qui fait foi. */
  const tensionActive = !!tarifs?.tension_active
  const [tension, setTension] = useState<TensionLue | null>(null)
  const latCollecte = aUnPoint(collecte) ? collecte.lat : null
  const lngCollecte = aUnPoint(collecte) ? collecte.lng : null
  const clientId = client?.id ?? null
  useEffect(() => {
    if (!tensionActive || latCollecte === null || lngCollecte === null) {
      setTension(null)
      return
    }
    let vivant = true
    const lire = () => void lireTension(orgId, latCollecte, lngCollecte, clientId).then((t) => vivant && setTension(t))
    lire()
    const minuteur = setInterval(lire, 30_000)
    return () => {
      vivant = false
      clearInterval(minuteur)
    }
  }, [orgId, tensionActive, latCollecte, lngCollecte, clientId])

  /* La route à moto (0066) : demandée dès que les deux points sont posés, MÉMORISÉE par le serveur pour le prix de la course.
     Tant qu'elle n'est pas là (ou si le service ne répond pas), l'aperçu prend l'estimation de secours, comme la base. */
  const cleActuelle = aUnPoint(collecte) && aUnPoint(depot) ? cleTrajet(collecte, depot) : null
  const [route, setRoute] = useState<{ cle: string; t: Trajet } | null>(null)
  useEffect(() => {
    if (!cleActuelle || !aUnPoint(collecte) || !aUnPoint(depot) || route?.cle === cleActuelle) return
    let vivant = true
    const de = { lat: collecte.lat, lng: collecte.lng }
    const a = { lat: depot.lat, lng: depot.lng }
    const minuteur = setTimeout(() => {
      void trajetRoute(de, a, orgId).then((t) => {
        if (t.memorisee) noterRouteMemorisee(de, a)
        if (vivant) setRoute({ cle: cleActuelle, t })
      })
    }, 300)
    return () => {
      vivant = false
      clearTimeout(minuteur)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- la clé résume les deux points
  }, [cleActuelle, orgId])
  const routeConnue = route && route.cle === cleActuelle && route.t.source === 'valhalla' ? route.t : null
  const routeEnCours = !!cleActuelle && route?.cle !== cleActuelle

  const apercu = useMemo(() => {
    if (!tarifs || !aUnPoint(collecte) || !aUnPoint(depot)) return null
    const prix = calculerPrixOpere(tarifs, {
      distance_km: routeConnue ? routeConnue.distance_km : distanceRepliKm(collecte.lat, collecte.lng, depot.lat, depot.lng),
      vehicule,
      option_longue_distance: client?.option_longue_distance ?? false,
      hors_creneau: !verdict.dedans,
      supplement_client_fcfa: client && client.type === 'pme' ? client.offslot_surcharge_fcfa : null,
      tension: tension?.comptes ?? null,
      au_forfait: tension?.au_forfait ?? false,
    })
    return { ...prix, distance_source: routeConnue ? ('route' as const) : ('estimation' as const) }
  }, [tarifs, collecte, depot, vehicule, client, verdict.dedans, tension, routeConnue])

  const peutDeroger = droits.role === 'owner' && !!apercu
  const prixDerogeNum = Number(prixDerogation.replace(/[\s\u00a0\u202f]/g, '').replace(',', '.'))
  const derogationValide = deroger && Number.isInteger(prixDerogeNum) && prixDerogeNum >= 0 && motifDerogation.trim().length >= 5
  const derogationActive = peutDeroger && deroger
  /* Prix ANNONCÉ au client : dérogation > calcul de la grille > saisie manuelle (sans coordonnées). */
  const total = derogationActive && derogationValide ? prixDerogeNum : apercu ? apercu.total : prixManuel + supplement
  const sansPoints = !apercu
  const bloqueSansPoints = sansPoints && !!tarifs && !accepteSansPoints

  const enAttente = donnees.courses.filter((c) => c.statut === 'creee')
  const disponibles = donnees.drivers.filter((d) => d.status === 'available')
  /* La course qu'on vient de créer, relue dans l'état EN DIRECT : la position envoyée par le destinataire s'y affiche. */
  const derniereCourse = derniere ? donnees.courses.find((c) => c.id === derniere.id) ?? null : null

  function choisir(c: Client) {
    setClient(c)
    setRequete(c.phone ?? c.name)
    /* La collecte habituelle du client (texte + épingle si elle a été posée) préremplit la collecte. */
    setCollecte({
      texte: c.default_pickup,
      repere: '',
      lat: c.default_pickup_lat,
      lng: c.default_pickup_lng,
      source: null,
    })
    setAdressesClient([])
    void adressesDuClient(orgId, c.id).then(setAdressesClient)
  }

  function reinitialiser() {
    setClient(null)
    setRequete('')
    setCollecte(ADRESSE_VIDE)
    setDepot(ADRESSE_VIDE)
    setDestinataire(SAISIE_DESTINATAIRE_VIDE)
    setAdressesClient([])
    setAdressesDest([])
    setAccepteSansPoints(false)
    setDeroger(false)
    setPrixDerogation('')
    setMotifDerogation('')
  }

  /* Adresses déjà connues du destinataire, retrouvées par SON téléphone (à défaut, celui du client). */
  const telephoneLivraison = !destinataire.clientRecoit && destinataire.telephone.trim() ? destinataire.telephone.trim() : (client?.phone ?? '')
  useEffect(() => {
    let vivant = true
    const t = setTimeout(() => {
      if (!client) return setAdressesDest([])
      void adressesDuTelephone(orgId, telephoneLivraison).then((a) => vivant && setAdressesDest(a))
    }, 350)
    return () => {
      vivant = false
      clearTimeout(t)
    }
  }, [orgId, client, telephoneLivraison])

  async function creer() {
    if (!client || enCours) return
    const problemes = verifierDestinataire(destinataire)
    if (problemes.length) {
      surNotifier(`Erreur : ${problemes[0]}`)
      return
    }
    if (derogationActive && !derogationValide) {
      surNotifier('Erreur : une dérogation demande un prix entier et un motif (5 caractères au moins).')
      return
    }
    setEnCours(true)
    try {
      const plusCode = (a: AdresseSaisie) => (aUnPoint(a) ? encoder(a.lat, a.lng) : null)
      /* La route doit être mémorisée AVANT l'insertion : c'est elle que la base facture (0066). Déjà faite par l'aperçu, en général. */
      if (aUnPoint(collecte) && aUnPoint(depot)) {
        await preparerRouteCourse(orgId, { lat: collecte.lat, lng: collecte.lng }, { lat: depot.lat, lng: depot.lng })
      }
      const annonce = total
      const creee = await creerCourse(orgId, {
        clientId: client.id,
        client,
        pickup: libelleAdresse(collecte, plusCode(collecte)),
        dropoff: libelleAdresse(depot, plusCode(depot)),
        ...(aUnPoint(collecte) ? { pickupLat: collecte.lat, pickupLng: collecte.lng } : {}),
        ...(aUnPoint(depot) ? { dropoffLat: depot.lat, dropoffLng: depot.lng } : {}),
        vehicule,
        total: annonce,
        surcharge: apercu ? apercu.supplement_horaire : supplement,
        channel: canal,
        destinataire: destinataireDepuisSaisie(destinataire),
        ...(derogationActive && derogationValide ? { derogation: { motif: motifDerogation } } : {}),
      })
      const id = creee.id

      /* Les points saisis alimentent le carnet d'adresses (collecte du client, livraison par téléphone du
         destinataire) — sans jamais retarder ni faire échouer la création. */
      if (aUnPoint(collecte)) {
        void memoriserAdresse(orgId, {
          clientId: client.id, telephone: null, label: client.name, texte: collecte.texte.trim(), repere: collecte.repere.trim(),
          lat: collecte.lat, lng: collecte.lng, source: collecte.source ?? 'epingle',
        })
      }
      if (aUnPoint(depot) && telephoneLivraison.trim()) {
        const dest = destinataireDepuisSaisie(destinataire)
        void memoriserAdresse(orgId, {
          clientId: null, telephone: telephoneLivraison.trim(), label: dest?.nom ?? client.name, texte: depot.texte.trim(),
          repere: depot.repere.trim(), lat: depot.lat, lng: depot.lng, source: depot.source ?? 'epingle',
        })
      }

      /* Auto-affectation : une PME servie DANS son créneau part toute seule au
         premier livreur disponible ; hors créneau, l'affectation reste manuelle
         puisqu'un supplément doit être annoncé.
         ⚠️ L'original conditionnait aussi cette règle à `billing_mode==='forfait'`,
         mais cette colonne N'EXISTE PAS en base : la condition était donc
         toujours vraie. On écrit ici la règle réellement appliquée, sans
         invoquer un champ qui n'existe pas. */
      let complement = ''
      if (client.type === 'pme' && verdict.dedans) {
        const libre = donnees.drivers.find((d) => d.status === 'available')
        if (libre) {
          try {
            await surAssignerAuto(id, libre.id)
            complement = ` · auto-assignée à ${libre.full_name}`
          } catch {
            complement = ' · auto-assignation refusée (à assigner)'
          }
        } else {
          complement = ' · aucun livreur dispo (à assigner)'
        }
      } else if (client.type === 'pme') {
        complement = ` · hors créneau : assignation manuelle${supplement ? ' + supplément' : ''}`
      }

      /* Le prix qui compte est celui de la BASE : on le relit dans la réponse de l'insertion. */
      const ecart = creee.prix !== annonce ? ` — ⚠ annoncé ${fcfa(annonce)}, la base a retenu ${fcfa(creee.prix)}` : ''
      surNotifier(
        `Course ${creee.ref ?? ''} créée pour ${client.name} : ${fcfa(creee.prix)}${creee.supplement ? ` (dont supplément ${fcfa(creee.supplement)})` : ''}${
          creee.majoration ? ` (forte demande : +${fcfa(creee.majoration)})` : ''
        }${
          creee.source === 'saisie' ? ' (prix saisi, sans coordonnées)' : ''
        }${ecart}${complement}`,
      )
      setDerniere(creee)
      reinitialiser()
      setFlash(id)
      await surRafraichir()
    } catch (e) {
      surNotifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setEnCours(false)
    }
  }

  /* L'affectation automatique passe par le même chemin que l'affectation
     manuelle — l'edge function, jamais un UPDATE direct. */
  async function surAssignerAuto(courseId: string, livreurId: string) {
    await assignerCourse(orgId, courseId, livreurId)
  }

  const creneauxDuJour = client?.slots.filter((x) => x.weekday === jourIso(new Date())) ?? []

  return (
    <>
      <Kpis donnees={donnees} />
      <div className="grid-2">
        <div>
          {/* ---------------- Qui appelle ? ---------------- */}
          <div className="card" data-anim>
            <div className="card-head">
              <div>
                <h3>
                  <span className="h-ic">
                    <Icone nom="phone" />
                  </span>{' '}
                  Qui appelle ?
                </h3>
                <div className="ch-sub">Tapez le numéro ou le nom — la fiche remonte toute seule</div>
              </div>
            </div>
            <div className="card-body">
              <div className="call-search">
                <div className="phone-in">
                  <Icone nom="phone" />
                  <input
                    placeholder="+223 76 … ou nom du client"
                    autoComplete="off"
                    value={requete}
                    onChange={(e) => setRequete(e.target.value)}
                  />
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                {!client && requete.trim().length >= 2 && resultats.length === 0 && (
                  <div className="no-hit">
                    Aucun client pour « {requete.trim()} »
                    {droits.peut('clients') && (
                      <div className="nh-actions">
                        <button className="btn btn-outline btn-sm" onClick={() => setNouveauClient('particulier')}>
                          <Icone nom="plus" /> Particulier
                        </button>
                        <button className="btn btn-gold btn-sm" onClick={() => setNouveauClient('pme')}>
                          <Icone nom="plus" /> Onboarder une PME
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {!client &&
                  resultats.map((c, i) => (
                    <div
                      className="client-hit"
                      key={c.id}
                      style={{ animationDelay: `${i * 45}ms` }}
                      onClick={() => choisir(c)}
                    >
                      <span className="avatar">{initiales(c.name)}</span>
                      <div>
                        <div className="h-name">{c.name}</div>
                        <div className="h-sub">
                          {c.phone ?? ''}
                          {c.quartier ? ` · ${c.quartier}` : ''}
                        </div>
                      </div>
                      <span className={`badge ${c.type === 'pme' ? 'b-gold' : 'b-blue'} plain`}>
                        {c.type === 'pme' ? 'PME' : 'Particulier'}
                      </span>
                    </div>
                  ))}
              </div>

              {client && (
                <div className="client-fiche">
                  <div className="cf-top">
                    <span className="avatar" style={{ width: 40, height: 40, fontSize: 14 }}>
                      {initiales(client.name)}
                    </span>
                    <div>
                      <div className="cf-name">{client.name}</div>
                      <div className="cf-sub">
                        {client.type === 'pme'
                          ? `PME${client.contact_name ? ` · ${client.contact_name}` : ''}`
                          : 'Particulier'}{' '}
                        · {client.phone ?? ''}
                      </div>
                    </div>
                    <button className="cf-close" onClick={reinitialiser} title="Fermer la fiche">
                      <Icone nom="x" />
                    </button>
                  </div>
                  {client.status === 'paused' && (
                    <div className="bandeau alerte" role="status" style={{ margin: '8px 0' }}>
                      <b>Client en pause.</b> Son abonnement s’arrête à la fin du mois en cours : vérifiez avant de créer une course.
                    </div>
                  )}
                  <div className="cf-grid">
                    <div className="cf-item">
                      <div className="l">Forfait</div>
                      <div className="v">{client.plan_code === 'particulier' ? 'Particulier (à la course)' : libelleDuPlan(client.plan_code, donnees.forfaits)}</div>
                    </div>
                    <div className="cf-item">
                      <div className="l">Collecte habituelle</div>
                      <div className="v">{client.default_pickup || '—'}</div>
                    </div>
                  </div>

                  {client.type === 'pme' && (
                    <>
                      <div className="slot-strip">
                        {creneauxDuJour.length === 0 ? (
                          <span className="slot-chip">Aucun créneau aujourd'hui</span>
                        ) : (
                          creneauxDuJour.map((x) => (
                            <span
                              key={`${x.weekday}-${x.start_time}`}
                              className={`slot-chip${verdict.creneau === x ? ' now' : ''}`}
                            >
                              Aujourd'hui {x.start_time.slice(0, 5)}–{x.end_time.slice(0, 5)}
                            </span>
                          ))
                        )}
                      </div>
                      <div className={`slot-verdict ${verdict.dedans ? 'ok' : 'off'}`}>
                        {verdict.dedans ? (
                          <>
                            <Icone nom="check" /> Dans le créneau préenregistré — tarif normal
                          </>
                        ) : (
                          <>
                            <Icone nom="clock" /> Hors créneau — supplément de{' '}
                            {fcfa(client.offslot_surcharge_fcfa)} à annoncer
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ---------------- Nouvelle course ---------------- */}
          <div className="card" data-anim>
            <div className="card-head">
              <div>
                <h3>
                  <span className="h-ic">
                    <Icone nom="package" />
                  </span>{' '}
                  Nouvelle course
                </h3>
                <div className="ch-sub">
                  {client
                    ? `Pour ${client.name}${client.type === 'pme' ? ' (PME)' : ' (particulier)'}`
                    : "Sélectionnez d'abord le client qui appelle"}
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="form form-grid">
                <div className="section-title">Collecte</div>
                <SelecteurAdresse
                  titre="Où récupérer le colis"
                  valeur={collecte}
                  surChange={setCollecte}
                  connues={adressesClient}
                  libelleConnues="Adresses déjà utilisées par ce client"
                  placeholderTexte="ex. Boutique Lafia, marché de Lafiabougou"
                  placeholderRepere="ex. face au marché, portail bleu"
                />

                <div className="section-title">Destinataire (si différent du client)</div>
                <div className="form-full">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={destinataire.clientRecoit}
                      onChange={(e) => setDestinataire({ ...destinataire, clientRecoit: e.target.checked })}
                    />
                    Le client reçoit lui-même
                  </label>
                </div>
                {!destinataire.clientRecoit && (
                  <>
                    <div className="field">
                      <label htmlFor="dest-nom">Nom du destinataire</label>
                      <input
                        id="dest-nom"
                        placeholder="ex. Awa Traoré"
                        maxLength={120}
                        autoComplete="off"
                        value={destinataire.nom}
                        onChange={(e) => setDestinataire({ ...destinataire, nom: e.target.value })}
                      />
                    </div>
                    <div className="field">
                      <label htmlFor="dest-tel">Téléphone du destinataire</label>
                      <input
                        id="dest-tel"
                        type="tel"
                        placeholder="+223 76 00 00 02"
                        maxLength={30}
                        autoComplete="off"
                        value={destinataire.telephone}
                        onChange={(e) => setDestinataire({ ...destinataire, telephone: e.target.value })}
                      />
                    </div>
                    <div className="form-full t-muted" style={{ fontSize: 12 }}>
                      Le livreur l'appelle lui, et les messages WhatsApp lui sont adressés. Sans téléphone, on joint le client.
                    </div>
                  </>
                )}

                <div className="section-title">Livraison</div>
                <SelecteurAdresse
                  titre="Où livrer le colis"
                  valeur={depot}
                  surChange={setDepot}
                  connues={adressesDest}
                  libelleConnues={`Adresses déjà connues pour ce numéro (${telephoneLivraison || 'aucun numéro'})`}
                  placeholderTexte="ex. Immeuble Baraka, 2e étage"
                  placeholderRepere="ex. derrière la mosquée, porte verte"
                />

                <div className="section-title">Demande</div>
                <div className="field">
                  <label>Canal</label>
                  <ListeDeroulante
                    valeur={canal}
                    options={[
                      { valeur: 'phone', libelle: 'Appel téléphonique' },
                      { valeur: 'whatsapp', libelle: 'WhatsApp' },
                      { valeur: 'other', libelle: 'Autre' },
                    ]}
                    surChangement={(v) => setCanal(v as Canal)}
                  />
                </div>
                <div className="field">
                  <label>Véhicule demandé</label>
                  <ListeDeroulante
                    valeur={vehicule}
                    options={VEHICULES.map((v) => ({ valeur: v, libelle: LIBELLE_VEHICULE[v] }))}
                    surChangement={(v) => setVehicule(v as TypeVehicule)}
                  />
                </div>

                <div className="section-title">Prix</div>
                <div className="form-full">
                  {apercu ? (
                    <>
                      <BilanPrix detail={apercu} dureeS={routeConnue?.duree_s ?? null} />
                      {routeEnCours && (
                        <div className="t-muted" style={{ fontSize: 12, marginTop: 6 }} aria-live="polite">
                          Calcul de l’itinéraire à moto…
                        </div>
                      )}
                      <div className="t-muted" style={{ fontSize: 12, marginTop: 6 }}>
                        Aperçu : la base recalcule ce prix à la création (grille, heure de Bamako) et c’est son résultat qui fait foi.
                        {donnees.tarifs?.grille_provisoire && ' La grille tarifaire est encore provisoire (Réglages).'}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="bandeau erreur" role="alert" style={{ marginBottom: 12 }}>
                        <strong>{tarifs ? 'Prix non calculé : il manque un point sur la carte.' : 'Grille tarifaire illisible : prix à saisir à la main.'}</strong>{' '}
                        {tarifs
                          ? `Posez l'épingle (ou saisissez un Plus Code) pour ${aUnPoint(collecte) ? 'la livraison' : aUnPoint(depot) ? 'la collecte' : 'la collecte et la livraison'} :
                            le prix sort alors de la grille, sans saisie. Si le destinataire n'est pas sûr de l'adresse, créez la course puis demandez-lui sa position.`
                          : "La migration 0030 n'est peut-être pas appliquée : le prix est celui que vous saisissez, sans calcul ni verrou."}
                      </div>
                      <div className="field" style={{ maxWidth: 260 }}>
                        <label>Prix saisi à la main (F)</label>
                        <input type="number" min={0} step={500} value={prixManuel} onChange={(e) => setPrixManuel(Number(e.target.value) || 0)} />
                      </div>
                      {supplement > 0 && (
                        <div className="price-line sur">
                          <span>Supplément hors créneau</span>
                          <span className="pl-val">+{fcfa(supplement)}</span>
                        </div>
                      )}
                      {tarifs && (
                        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 10, fontWeight: 600, cursor: 'pointer' }}>
                          <input type="checkbox" style={{ width: 'auto', marginTop: 3 }} checked={accepteSansPoints} onChange={(e) => setAccepteSansPoints(e.target.checked)} />
                          <span>
                            Je crée la course sans les deux points : le prix est celui que je saisis, marqué « saisie » dans la base,
                            et recalculé si les coordonnées arrivent avant l'assignation.
                          </span>
                        </label>
                      )}
                    </>
                  )}
                </div>

                {peutDeroger && (
                  <div className="form-full">
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600, cursor: 'pointer' }}>
                      <input type="checkbox" style={{ width: 'auto' }} checked={deroger} onChange={(e) => setDeroger(e.target.checked)} />
                      Déroger au prix de la grille (propriétaire)
                    </label>
                    {deroger && (
                      <div className="form form-grid" style={{ marginTop: 8 }}>
                        <div className="field">
                          <label htmlFor="derog-prix">Prix accordé (F)</label>
                          <input id="derog-prix" inputMode="numeric" value={prixDerogation} onChange={(e) => setPrixDerogation(e.target.value)} />
                        </div>
                        <div className="field">
                          <label htmlFor="derog-motif">Motif (obligatoire, conservé avec la course)</label>
                          <input id="derog-motif" maxLength={300} value={motifDerogation} onChange={(e) => setMotifDerogation(e.target.value)} placeholder="ex. geste commercial, client fidèle" />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {(!apercu || derogationActive) && (
                  <div className="form-full price-line total" style={{ marginTop: 4 }}>
                    <span>{derogationActive ? 'Prix accordé, à annoncer au client' : 'À annoncer au client'}</span>
                    <span className="pl-val">{fcfa(total)}</span>
                  </div>
                )}

                <div
                  className="form-full"
                  style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 2, flexWrap: 'wrap' }}
                >
                  <button
                    className="btn btn-gold"
                    style={{ width: 'auto' }}
                    onClick={() => void creer()}
                    disabled={!client || enCours || !peutCreer || bloqueSansPoints || (derogationActive && !derogationValide)}
                  >
                    <Icone nom="plus" /> {enCours ? 'Création…' : sansPoints && tarifs ? 'Créer sans coordonnées' : 'Créer la course'}
                  </button>
                  {!peutCreer ? (
                    <span className="t-muted">— {droits.refus}</span>
                  ) : !client ? (
                    <span className="t-muted">— identifiez d'abord l'appelant</span>
                  ) : bloqueSansPoints ? (
                    <span className="t-muted">— placez les deux points, ou cochez la case ci-dessus</span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {derniereCourse && derniere && (
            <div className="card" data-anim>
              <div className="card-head">
                <div>
                  <h3>
                    <span className="h-ic">
                      <Icone nom="check" />
                    </span>{' '}
                    Course {derniere.ref ?? ''} créée
                  </h3>
                  <div className="ch-sub">
                    Prix retenu par la base : {fcfa(derniere.prix)}
                    {derniere.distanceKm != null ? ` · ${String(derniere.distanceKm).replace('.', ',')} km` : ''}
                    {derniere.source === 'saisie' && ' · prix saisi (sans coordonnées)'}
                    {derniere.source === 'override' && ' · prix dérogé'}
                    {derniere.majoration > 0 && ` · forte demande : +${fcfa(derniere.majoration)}`}
                  </div>
                </div>
                <span className="sp" />
                <button className="btn btn-ghost btn-sm" onClick={() => setDerniere(null)} title="Masquer">
                  <Icone nom="x" />
                </button>
              </div>
              <div className="card-body">
                <DemandePosition
                  course={derniereCourse}
                  telephoneClient={donnees.clients.find((c) => c.id === derniereCourse.client_account_id)?.phone ?? null}
                  marque={donnees.org.brand_name || donnees.org.name}
                  surNotifier={surNotifier}
                />
              </div>
            </div>
          )}
        </div>

        {/* ---------------- Colonne droite ---------------- */}
        <div>
          <div className="card" data-anim>
            <div className="card-head">
              <div>
                <h3>
                  <span className="h-ic">
                    <Icone nom="dispatch" />
                  </span>{' '}
                  À dispatcher
                </h3>
                <div className="ch-sub">
                  {enAttente.length} course{s(enAttente.length)} en attente d'assignation
                </div>
              </div>
              <span className="sp" />
              <button className="btn btn-ghost btn-sm" onClick={surAllerDispatch}>
                Tout voir <Icone nom="arrow" />
              </button>
            </div>
            <div className="card-body">
              <div className="course-list">
                {enAttente.length === 0 ? (
                  <div className="no-hit">Aucune course en attente</div>
                ) : (
                  enAttente.map((c) => (
                    <CarteCourse
                      key={c.id}
                      course={c}
                      donnees={donnees}
                      compacte
                      flash={flash === c.id}
                      surAssigner={surAssigner}
                      surAvancer={surAvancer}
                      surAnnuler={surAnnuler}
                    />
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="card" data-anim>
            <div className="card-head">
              <div>
                <h3>
                  <span className="h-ic">
                    <Icone nom="user" />
                  </span>{' '}
                  Livreurs disponibles
                </h3>
                <div className="ch-sub">
                  {disponibles.length} disponible{s(disponibles.length)} / {donnees.drivers.length}
                </div>
              </div>
            </div>
            <div className="card-body flush">
              <div className="tbl-wrap">
                <table>
                  <tbody>
                    {donnees.drivers.map((d) => {
                      const etat = ETATS_LIVREUR[d.status] ?? ['—', 'b-gray']
                      return (
                        <tr key={d.id}>
                          <td>
                            <div className="u-cell">
                              <span className="avatar">{initiales(d.full_name)}</span>
                              <span className="t-strong">{d.full_name}</span>
                            </div>
                          </td>
                          <td>
                            <span className={`badge ${etat[1]}`}>{etat[0]}</span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {nouveauClient && (
        <ModaleClient
          type={nouveauClient}
          orgId={orgId}
          forfaits={donnees.forfaits}
          prerempli={requete}
          surFermer={() => setNouveauClient(null)}
          surNotifier={surNotifier}
          surCree={async (message, id) => {
            setNouveauClient(null)
            surNotifier(message)
            setASelectionner(id)
            await surRafraichir()
          }}
        />
      )}
    </>
  )
}
